import { useCallback, useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { DateRangeType } from "react-tailwindcss-datepicker";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper, RowModel, SortingState } from "@tanstack/react-table";

import useEbay from "../../data/useEbay";
import { IPart } from "../../models/Part";
import { PageMode } from "../../models/PagedTable";
import { IWarehouseLocation } from "../../models/Location";
import { IBulkIntelligencePricingResult, ISerpApiSearchResult } from "../../models/PartLookup";
import PagedTable from "../shared/Tables/PagedTable";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import DateRangeFilter from "../shared/DateRangeFilter";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import useNotification from "../notifications/useNotifications";
import useCategoriesPartial from "../../data/useCategoriesPartial";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useListingOptimisation from "../../data/useListingOptimisation";
import useTemplateProductDescription from "../../data/useTemplateProductDescription";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import WarehouseLocationSearch from "../warehouseLocations/WarehouseLocationSearch";
import VehiclePartSearchFilters, {
  buildVehiclePartSearchBody,
  defaultVehicleDetailFilterEnabled,
  IVehiclePartSearchFields,
  VehicleDetailFilterKey,
  vehiclePartSearchDefaultValues,
} from "../stock/VehiclePartSearchFilters";
import TableFilterPanel from "../shared/Tables/TableFilterPanel";
import TableDateSortControl, { LISTED_DATE_SORT_ID } from "../shared/Tables/TableDateSortControl";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import classNames from "../../utils/classNames";
import toIsoDate from "../../utils/toIsoDate";
import { MISSING_FIELD_OPTIONS } from "../ebayListings/listedTable/missingFields";
import UnlistedImageCell from "../ebayListings/unlistedTable/UnlistedImageCell";
import UnlistedPartTypeCell from "../ebayListings/unlistedTable/UnlistedPartTypeCell";
import UnlistedDonorVehicleCell from "../ebayListings/unlistedTable/UnlistedDonorVehicleCell";
import UnlistedEbayTitle from "../ebayListings/unlistedTable/UnlistedEbayTitle";
import WilmaTitleRow from "../ebayListings/WilmaTitleRow";
import ListingRecommendationsCell from "../ebayListings/ListingRecommendationsCell";
import { computeListingScore } from "../ebayListings/listedTable/listingScore";
import { computeEbayListingTitle } from "../ebayListings/EbayListingTitle";
import BulkEditListingSpecifics from "../ebayListings/BulkEditListingSpecifics";
import BulkUpdatePoliciesModal, { IBulkUpdatePolicies } from "../ebayListings/listedTable/BulkUpdatePoliciesModal";
import BulkRelistResultModal, { BulkRelistResultData } from "../ebayListings/BulkRelistResultModal";
import Modal from "../../layouts/Modal";
import StageOptimisationModal from "./StageOptimisationModal";
import OptimisationStagedCell from "./OptimisationStagedCell";
import OptimisationLiveListingCell from "./OptimisationLiveListingCell";
import OptimisationImagesRow from "./OptimisationImagesRow";
import {
  IListingOptimisation,
  IStageListingOptimisationItem,
  ListingOptimisationStatus,
} from "../../models/listingOptimisation/IListingOptimisation";

const colHelper = createColumnHelper<IPart>();

// Stable reference so the additionalBody effect doesn't re-run every render when the prop is omitted
const noMissingFields: string[] = [];

type LocationSearchMode = "equals" | "beginsWith";

interface IFilterFormFields extends IVehiclePartSearchFields {
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
}

const defaultFilterFormValues: IFilterFormFields = {
  ...vehiclePartSearchDefaultValues,
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created"
};

// Only the filters owned by this component are saved - the missing field chips live on the
// page (shared with the stats tiles), so they stay out of the saved defaults.
interface IOptimisationSavedDefaultFilters {
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationBeginsWith: string;
  startDate: string | null;
  endDate: string | null;
  dateFieldType: string;
  // Optional so filters saved before the sort control existed still apply
  sorting?: SortingState;
}

// Oldest listings first - the server maps this id onto the displayed offer's listing date
// (falling back to listedDate), which is the age shown on each row.
const defaultSorting: SortingState = [{ id: LISTED_DATE_SORT_ID, desc: false }];

interface IEbayOptimisationTableProps {
  selectedMissingFields?: string[];
  onToggleMissingField?: (key: string) => void;
}

export default function EbayOptimisationTable({
  selectedMissingFields = noMissingFields,
  onToggleMissingField,
}: IEbayOptimisationTableProps = {}) {
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Staged changes");
  const { saveHandlers: descriptionSaveHandlers } = useDefaultCRUDHandlers("Descriptions");
  const { categories: allCategories } = useCategoriesPartial();
  const { warehouseLocations } = useWarehouseLocations();
  const { policies, locations } = useEbay();
  const { pendingOptimisations, stageChanges, discardChanges, sendToRelist } = useListingOptimisation();
  const { bulkUpdateProductDescription } = useTemplateProductDescription();
  // Empty partId — this hook is used here only for the bulk intelligence variant
  const { intelligencePricingBulk } = usePartsLookupMutations("");
  const queryClient = useQueryClient();

  const [stageModalOpen, setStageModalOpen] = useState<boolean>(false);
  const [stagePart, setStagePart] = useState<IPart | null>(null);

  const [policyModalOpen, setPolicyModalOpen] = useState<boolean>(false);
  const [aspectsModalOpen, setAspectsModalOpen] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<RowModel<IPart> | null>(null);

  const [discardConfirmOpen, setDiscardConfirmOpen] = useState<boolean>(false);
  // Separate loading flag because intelligencePricingBulk.isLoading resets between chunks
  const [intelligenceRunning, setIntelligenceRunning] = useState(false);

  // Dedicated relist confirm so it can carry the Append SKU option (same as the
  // Active Listings table), followed by the per-part results modal.
  const [relistConfirmOpen, setRelistConfirmOpen] = useState<boolean>(false);
  const [relistAppendSku, setRelistAppendSku] = useState<boolean>(false);
  const [relistResult, setRelistResult] = useState<BulkRelistResultData | null>(null);
  const [relistResultOpen, setRelistResultOpen] = useState<boolean>(false);

  // Pending staged changes keyed by part so table rows can be merged client-side
  const optimisationsByPartId = useMemo(() => {
    const map = new Map<string, IListingOptimisation>();
    pendingOptimisations.data?.data
      ?.filter((o) => o.status === ListingOptimisationStatus.Pending)
      .forEach((o) => map.set(o.partDataId, o));
    return map;
  }, [pendingOptimisations.data]);

  // Same filter bar as the Active Listings table - category, warehouse location
  // and date range, passed to /part/listings through additionalBody.
  const { register: registerFilters, setValue: setFilterValue, watch: watchFilters, getValues: getFilterValues, reset: resetFilters } = useForm<IFilterFormFields>({
    defaultValues: defaultFilterFormValues,
  });

  // The category/vehicle inputs keep their selection in internal state, so the search block has
  // to be remounted when those values are cleared from outside the inputs (pill / Clear button).
  const [vehicleFiltersKey, setVehicleFiltersKey] = useState(0);

  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [isSearching, setIsSearching] = useState(false);
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [isSmartMatch, setIsSmartMatch] = useState(false);
  const [vehicleDetailFilterEnabled, setVehicleDetailFilterEnabled] = useState<Record<VehicleDetailFilterKey, boolean>>(defaultVehicleDetailFilterEnabled);

  const watchMake = watchFilters("make");
  const watchModel = watchFilters("model");
  const watchTrim = watchFilters("trim");
  const watchYearFrom = watchFilters("yearFrom");
  const watchYearTo = watchFilters("yearTo");
  const watchBodyStyle = watchFilters("bodyStyle");
  const watchNumberOfDoors = watchFilters("numberOfDoors");
  const watchFuelType = watchFilters("fuelType");
  const watchEngineSize = watchFilters("engineSize");
  const watchEngineCode = watchFilters("engineCode");
  const watchTransmission = watchFilters("transmissionType");
  const watchPartNumber = watchFilters("partNumber");
  const watchTagNumber = watchFilters("tagNumber");
  const watchVrm = watchFilters("vrm");
  const watchPartCategory = watchFilters("partCategory");
  const watchWarehouseLocation = watchFilters("warehouseLocation");
  const watchLocationSearchMode = watchFilters("locationSearchMode");
  const watchWarehouseLocationBeginsWith = watchFilters("warehouseLocationBeginsWith");

  useEffect(() => {
    // Smart Match searches by the looked-up vehicle instead of the individual vehicle fields,
    // so the rest of the vehicle body is dropped while it is active.
    const vehiclePartSearchBody = isSmartMatch
      ? {
        smartSearch: watchVrm,
        categoryIds: watchPartCategory?.map((category) => category.id),
      }
      : buildVehiclePartSearchBody(getFilterValues(), vehicleDetailFilterEnabled);

    setAdditionalBody({
      ...vehiclePartSearchBody,
      locationSearchMode: watchLocationSearchMode,
      warehouseLocationId: watchLocationSearchMode === "equals" ? watchWarehouseLocation?.id : undefined,
      warehouseLocationBeginsWith: watchLocationSearchMode === "beginsWith" ? watchWarehouseLocationBeginsWith || undefined : undefined,
      startDate: dates.startDate ? toIsoDate(dates.startDate) : undefined,
      endDate: dates.endDate ? toIsoDate(dates.endDate) : undefined,
      dateFieldType: dateFieldType,
      missingFields: selectedMissingFields.length > 0 ? selectedMissingFields : undefined,
    });
  }, [getFilterValues, isSmartMatch, vehicleDetailFilterEnabled, watchMake, watchModel, watchTrim, watchYearFrom, watchYearTo, watchBodyStyle, watchNumberOfDoors, watchFuelType, watchEngineSize, watchEngineCode, watchTransmission, watchPartNumber, watchTagNumber, watchVrm, watchPartCategory, watchWarehouseLocation, watchLocationSearchMode, watchWarehouseLocationBeginsWith, dates, dateFieldType, selectedMissingFields]);

  // Disable the filter inputs while the table query is in flight (same behaviour
  // as the Active Listings table).
  const [queryState, setQueryState] = useState<any>(null);

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache.getAll().filter(query => query.queryKey[0] === "pagedTable");
      const latestQuery = pagedTableQueries[pagedTableQueries.length - 1];
      setQueryState(latestQuery?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const isSearchDisabled = useMemo(
    () => !queryState || queryState.status === "loading" || isSearching,
    [queryState, isSearching]
  );

  const handleClearFilters = () => {
    resetFilters(defaultFilterFormValues);
    setIsSmartMatch(false);
    setDates({ startDate: null, endDate: null });
    setDateFieldType("Created");
    setSorting(defaultSorting);
    setVehicleFiltersKey((key) => key + 1);
    selectedMissingFields.forEach((key) => onToggleMissingField?.(key));
  };

  const activeFilterPills = useMemo(() => {
    const pills: { key: string; label: string; onClear: () => void }[] = [];

    if (watchPartCategory?.length) {
      pills.push({
        key: "partCategory",
        label: `Categories: ${watchPartCategory.length}`,
        onClear: () => {
          setFilterValue("partCategory", [] as any);
          setVehicleFiltersKey((key) => key + 1);
        },
      });
    }

    if (watchWarehouseLocation?.id || watchWarehouseLocationBeginsWith) {
      pills.push({
        key: "warehouseLocation",
        label: `Location: ${watchWarehouseLocation?.displayName ?? watchWarehouseLocationBeginsWith}`,
        onClear: () => {
          setFilterValue("warehouseLocation", undefined as any);
          setFilterValue("warehouseLocationId", undefined as any);
          setFilterValue("warehouseLocationBeginsWith", "" as any);
        },
      });
    }

    if (dates.startDate || dates.endDate) {
      pills.push({
        key: "dates",
        label: `Dates: ${dateFieldType}`,
        onClear: () => setDates({ startDate: null, endDate: null }),
      });
    }

    if (onToggleMissingField) {
      selectedMissingFields.forEach((field) => {
        pills.push({
          key: `missing-${field}`,
          label: `Missing: ${MISSING_FIELD_OPTIONS.find((option) => option.key === field)?.label ?? field}`,
          onClear: () => onToggleMissingField(field),
        });
      });
    }

    return pills;
  }, [watchPartCategory, watchWarehouseLocation?.id, watchWarehouseLocation?.displayName, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, selectedMissingFields, onToggleMissingField, setFilterValue]);

  const getDefaultFilters = useCallback((): IOptimisationSavedDefaultFilters => ({
    locationSearchMode: watchLocationSearchMode,
    warehouseLocation: watchWarehouseLocation,
    warehouseLocationBeginsWith: watchWarehouseLocationBeginsWith,
    startDate: dates.startDate ? toIsoDate(dates.startDate) : null,
    endDate: dates.endDate ? toIsoDate(dates.endDate) : null,
    dateFieldType,
    sorting,
  }), [watchLocationSearchMode, watchWarehouseLocation, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, sorting]);

  const applyDefaultFilters = useCallback((savedFilters: IOptimisationSavedDefaultFilters) => {
    setFilterValue("locationSearchMode", (savedFilters.locationSearchMode ?? "equals") as any);
    setFilterValue("warehouseLocation", savedFilters.warehouseLocation as any);
    setFilterValue("warehouseLocationId", savedFilters.warehouseLocation?.id as any);
    setFilterValue("warehouseLocationBeginsWith", (savedFilters.warehouseLocationBeginsWith ?? "") as any);

    setDates({
      startDate: savedFilters.startDate ? new Date(savedFilters.startDate) : null,
      endDate: savedFilters.endDate ? new Date(savedFilters.endDate) : null,
    });
    setDateFieldType(savedFilters.dateFieldType ?? "Created");
    if (savedFilters.sorting?.length) setSorting(savedFilters.sorting);
  }, [setFilterValue]);

  const handleOpenStageModal = (part: IPart) => {
    setStagePart(part);
    setStageModalOpen(true);
  };

  const handleStageSubmit = (item: IStageListingOptimisationItem) => {
    stageChanges.mutate([item], {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setStageModalOpen(false);
        setStagePart(null);
      },
    });
  };

  const getSelected = (rows: RowModel<IPart> | null) =>
    rows?.rows.filter((row) => row.getIsSelected()) ?? [];

  const handleOpenPolicyModal = (rows: RowModel<IPart>) => {
    if (getSelected(rows).length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }
    setSelectedRows(rows);
    setPolicyModalOpen(true);
  };

  // Staging replaces the whole record, so bulk actions carry over any previously
  // staged values rather than wiping them with nulls.
  const buildCarryOverItem = (partId: string): IStageListingOptimisationItem => {
    const existing = optimisationsByPartId.get(partId);
    return {
      partDataId: partId,
      newTitle: existing?.newTitle ?? null,
      newPrice: existing?.newPrice ?? null,
      newFulfillmentPolicyId: existing?.newFulfillmentPolicyId ?? null,
      newPaymentPolicyId: existing?.newPaymentPolicyId ?? null,
      newReturnPolicyId: existing?.newReturnPolicyId ?? null,
      newLocationKey: existing?.newLocationKey ?? null,
      applyImageRules: existing?.applyImageRules ?? false,
      removeBackgroundFirstImage: existing?.removeBackgroundFirstImage ?? false,
      applyCategoryDefaults: existing?.applyCategoryDefaults ?? false,
    };
  };

  // Inline title manager stages only — live eBay title stays until Send to Relist.
  const handleStageTitle = (part: IPart, title: string) => {
    const liveTitle = (part.offers?.[0]?.title ?? "").trim();
    const trimmed = title.trim();
    if (!trimmed) return;

    stageChanges.mutate(
      [
        {
          ...buildCarryOverItem(part.id),
          newTitle: trimmed !== liveTitle ? trimmed : null,
        },
      ],
      saveHandlers
    );
  };

  const handleBulkStagePolicies = (policiesData: IBulkUpdatePolicies) => {
    const selected = getSelected(selectedRows);

    const items: IStageListingOptimisationItem[] = selected.map((row) => ({
      ...buildCarryOverItem(row.original.id),
      newFulfillmentPolicyId: policiesData.fulfillmentPolicyId ?? optimisationsByPartId.get(row.original.id)?.newFulfillmentPolicyId ?? null,
      newPaymentPolicyId: policiesData.paymentPolicyId ?? optimisationsByPartId.get(row.original.id)?.newPaymentPolicyId ?? null,
      newReturnPolicyId: policiesData.returnPolicyId ?? optimisationsByPartId.get(row.original.id)?.newReturnPolicyId ?? null,
      newLocationKey: policiesData.locationKey ?? optimisationsByPartId.get(row.original.id)?.newLocationKey ?? null,
    }));

    stageChanges.mutate(items, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setPolicyModalOpen(false);
        setSelectedRows(null);
      },
    });
  };

  // Bulk stage a relist-time flag against the selected rows, keeping everything else already staged.
  const handleBulkStageFlag = (
    rows: RowModel<IPart>,
    flag: "applyImageRules" | "removeBackgroundFirstImage" | "applyCategoryDefaults"
  ) => {
    const selected = getSelected(rows);

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    const items: IStageListingOptimisationItem[] = selected.map((row) => ({
      ...buildCarryOverItem(row.original.id),
      [flag]: true,
    }));

    stageChanges.mutate(items, saveHandlers);
  };

  // Bulk stage the Wilma-generated title (from category defaults / merge tags) as
  // the new title - the current eBay title stays live until the relist happens.
  const handleBulkStageWilmaTitles = (rows: RowModel<IPart>) => {
    const selected = getSelected(rows);

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    const items: IStageListingOptimisationItem[] = [];

    selected.forEach((row) => {
      const wilmaTitle = (computeEbayListingTitle(row.original, null, true) ?? "").trim();

      if (!wilmaTitle) return;

      const base = buildCarryOverItem(row.original.id);

      items.push({
        ...base,
        newTitle: wilmaTitle !== row.original.offers?.at(0)?.title ? wilmaTitle : base.newTitle,
      });
    });

    if (items.length === 0) {
      addNotification({ primaryText: "No Wilma titles could be generated for the selected listings", variant: "warn" });
      return;
    }

    stageChanges.mutate(items, saveHandlers);
  };

  // Regenerates the product description from the template against the part record only -
  // updateOffer stays false so the live eBay listing is untouched and the new description
  // flows onto the new listing at relist (same as the aspect edits).
  const handleBulkResetDescription = (rows: RowModel<IPart>) => {
    const selected = getSelected(rows);

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    const partIds = selected.map((row) => row.original.id);

    bulkUpdateProductDescription.mutate({ partIds }, descriptionSaveHandlers);
  };

  const handleOpenAspectsModal = (rows: RowModel<IPart>) => {
    if (getSelected(rows).length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }
    setSelectedRows(rows);
    setAspectsModalOpen(true);
  };

  // Writes intelligence results into all active pagedTable query caches so the
  // table updates immediately without waiting for a refetch.
  const applyIntelligenceToCache = (
    partId: string,
    summary: { min: number; max: number; avg: number } | null,
    counts: { active: number; sold: number }
  ) => {
    queryClient.getQueriesData(["pagedTable"]).forEach(([key]) => {
      queryClient.setQueryData(key, (current: any) => {
        if (!current?.data?.rows) return current;
        return {
          ...current,
          data: {
            ...current.data,
            rows: current.data.rows.map((row: any) =>
              row.id === partId
                ? {
                    ...row,
                    intelligenceMin: summary?.min ?? null,
                    intelligenceAverage: summary?.avg ?? null,
                    intelligenceMax: summary?.max ?? null,
                    intelligenceActiveCount: counts.active,
                    intelligenceSoldCount: counts.sold,
                    lastIntelligenceLookup: new Date().toISOString(),
                  }
                : row
            ),
          },
        };
      });
    });
  };

  const summarisePrices = (results: ISerpApiSearchResult[]) => {
    const prices = results
      .map((r) => r.priceExtracted)
      .filter((p): p is number => p != null && p > 0);

    if (prices.length === 0) return null;

    const round = (n: number) => Math.round(n * 100) / 100;
    return {
      min: round(Math.min(...prices)),
      max: round(Math.max(...prices)),
      avg: round(prices.reduce((sum, p) => sum + p, 0) / prices.length),
    };
  };

  const handleBulkRunIntelligence = async (rows: RowModel<IPart>) => {
    const selected = getSelected(rows);

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    setIntelligenceRunning(true);

    try {
      const partIds = selected.map((row) => row.original.id);
      const response = await intelligencePricingBulk.mutateAsync(partIds);
      const bulkResults: IBulkIntelligencePricingResult[] = response?.data ?? [];

      let succeeded = 0;
      let failed = 0;

      bulkResults.forEach((item) => {
        const results = item.results ?? [];
        const summary = summarisePrices(results);
        const counts = {
          active: results.filter((r) => !r.isSold).length,
          sold: results.filter((r) => r.isSold).length,
        };
        applyIntelligenceToCache(item.partId, summary, counts);
        if (results.length > 0) succeeded += 1;
        else failed += 1;
      });

      addNotification({
        primaryText: `Intelligence updated for ${succeeded} part${succeeded === 1 ? "" : "s"}${failed > 0 ? `, ${failed} returned no data` : ""}`,
        variant: failed > 0 && succeeded === 0 ? "warn" : "success",
      });
    } catch {
      addNotification({ primaryText: "Intelligence pricing failed", variant: "error" });
    } finally {
      setIntelligenceRunning(false);
    }
  };

  // Stage the intelligence average as newPrice - never updates the live offer.
  const handleStageAvgPrice = (partId: string, avgPrice: number) => {
    if (!avgPrice || avgPrice <= 0) return;

    stageChanges.mutate(
      [{ ...buildCarryOverItem(partId), newPrice: Math.round(avgPrice * 100) / 100 }],
      saveHandlers
    );
  };

  const handleOpenDiscardConfirm = (rows: RowModel<IPart>) => {
    const selected = getSelected(rows);

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    if (!selected.some((row) => optimisationsByPartId.has(row.original.id))) {
      addNotification({ primaryText: "No staged changes on the selected listings", variant: "warn" });
      return;
    }

    setSelectedRows(rows);
    setDiscardConfirmOpen(true);
  };

  const handleDiscard = () => {
    const selected = getSelected(selectedRows);

    setDiscardConfirmOpen(false);
    setSelectedRows(null);

    const partDataIds = selected
      .map((row) => row.original.id)
      .filter((id) => optimisationsByPartId.has(id));

    discardChanges.mutate(partDataIds, {
      onSuccess: () => {
        addNotification({ primaryText: "Staged changes discarded", variant: "success" });
      },
      onError: () => {
        addNotification({ primaryText: "Failed to discard staged changes", variant: "error" });
      },
    });
  };

  const handleOpenRelistConfirm = (rows: RowModel<IPart>) => {
    if (getSelected(rows).length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    setSelectedRows(rows);
    setRelistAppendSku(false);
    setRelistConfirmOpen(true);
  };

  const handleSendToRelist = () => {
    const selected = getSelected(selectedRows);

    setRelistConfirmOpen(false);
    setSelectedRows(null);

    if (selected.length === 0) {
      addNotification({ primaryText: "No parts selected", variant: "warn" });
      return;
    }

    const partDataIds = selected.map((row) => row.original.id);

    sendToRelist.mutate(
      { partDataIds, appendSku: relistAppendSku },
      {
        // The relist result carries a per-part breakdown of successes/failures with
        // reasons, so show that in the dedicated modal instead of a generic toast.
        onSuccess: (response) => {
          setRelistResult(response.data);
          setRelistResultOpen(true);
        },
        onError: () => {
          addNotification({ primaryText: "Failed to send listings to be relisted", variant: "error" });
        },
      }
    );
  };

  const expandedRow = (part: IPart) => {
    return <OptimisationImagesRow part={part} />;
  };

  const cols = useMemo(
    () => [
      // The expanded panel is the photo manager only. Image edits apply to the part
      // straight away - they sit outside the stage -> relist flow.
      TableCheckboxCol(true, true, true, "ebay-optimisation", true),
      colHelper.display({
        id: "images",
        meta: {
          thClassName: "min-w-[12rem] w-52 !p-1.5",
          tdClassName: "min-w-[12rem] w-52 !p-1.5 align-top h-px",
        },
        cell: (info) => <UnlistedImageCell part={info.row.original} />,
      }),
      colHelper.accessor("categoryId", {
        header: "Part Information",
        id: "categoryId",
        enableSorting: true,
        sortDescFirst: false,
        sortingFn: "auto",
        meta: {
          thClassName: "min-w-[24rem] max-w-[30rem] w-[26rem] !p-1.5",
          tdClassName: "min-w-[24rem] max-w-[30rem] w-[26rem] align-top h-px !p-1.5",
        },
        cell: (info) => (
          <UnlistedPartTypeCell part={info.row.original} categories={allCategories.data?.data} />
        ),
      }),
      colHelper.display({
        id: "vrm",
        header: "Donor vehicle",
        // Server-side sorting has no case for the vehicle VRM, so a sortable header would
        // silently fall back to the created-date sort - leave it unsortable instead.
        enableSorting: false,
        meta: {
          thClassName: "min-w-[10rem] max-w-[15rem] w-[11rem] !p-1.5",
          tdClassName: "min-w-[10rem] max-w-[15rem] w-[11rem] align-top h-px !p-1.5",
        },
        cell: (info) => <UnlistedDonorVehicleCell part={info.row.original} />,
      }),
      colHelper.accessor(
        (row) => {
          const offer = row.offers?.[0];
          return offer?.buyItNowPrice ?? offer?.auctionStartPrice ?? row.enteredPrice ?? 0;
        },
        {
          id: "offer.price",
          header: "Live Listing",
          enableSorting: true,
          sortDescFirst: true,
          meta: {
            thClassName: "min-w-[18rem] w-[20rem] !p-1.5",
            tdClassName: "min-w-[18rem] w-[20rem] align-top h-px !p-1.5",
          },
          cell: (info) => (
            <OptimisationLiveListingCell
              part={info.row.original}
              onStageAvgPrice={handleStageAvgPrice}
              stagingPrice={stageChanges.isLoading}
            />
          ),
        }
      ),
      colHelper.accessor((row) => computeListingScore(row).percent, {
        id: "recommendations",
        header: "eBay Snapshot",
        enableSorting: true,
        sortDescFirst: false,
        sortingFn: "basic",
        meta: {
          thClassName: "min-w-[26rem] w-[28rem] !p-1.5",
          tdClassName: "min-w-[26rem] w-[28rem] align-top h-px !p-1.5",
        },
        cell: (info) => <ListingRecommendationsCell part={info.row.original} />,
      }),
      colHelper.display({
        id: "stagedChanges",
        header: "Staged Changes",
        enableSorting: false,
        meta: {
          thClassName: "min-w-[16rem] w-[18rem] !p-1.5",
          tdClassName: "min-w-[16rem] w-[18rem] align-top h-px !p-1.5",
        },
        cell: (info) => (
          <OptimisationStagedCell
            part={info.row.original}
            optimisation={optimisationsByPartId.get(info.row.original.id)}
            policies={policies.data?.data}
            locations={locations.data?.data}
            onEdit={() => handleOpenStageModal(info.row.original)}
          />
        ),
      }),
    ],
    [allCategories.data, optimisationsByPartId, policies.data, locations.data, stageChanges.isLoading] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <>
      <StageOptimisationModal
        open={stageModalOpen}
        onClose={() => { setStageModalOpen(false); setStagePart(null); }}
        part={stagePart}
        optimisation={stagePart ? optimisationsByPartId.get(stagePart.id) : undefined}
        onSubmit={handleStageSubmit}
        isLoading={stageChanges.isLoading}
      />

      <BulkUpdatePoliciesModal
        open={policyModalOpen}
        onClose={() => { setPolicyModalOpen(false); setSelectedRows(null); }}
        onSubmit={handleBulkStagePolicies}
        isLoading={stageChanges.isLoading}
        title={"Stage eBay Policy Changes"}
        description={"Selected policies are staged against the selected listings and applied when they are sent to be relisted - nothing is sent to eBay yet. Leave fields empty to keep existing values."}
        submitLabel={"Stage Policies"}
        showLocation={true}
      />

      {/* Local-only aspect edits (updateOffer=false) - they flow onto the new listing at relist */}
      <Modal open={aspectsModalOpen} setOpen={setAspectsModalOpen} width="max-w-7xl">
        {selectedRows && (
          <BulkEditListingSpecifics
            parts={selectedRows.rows.filter((row) => row.getIsSelected()).map((row) => row.original)}
            onClose={() => { setAspectsModalOpen(false); setSelectedRows(null); }}
            updateOffer={false}
          />
        )}
      </Modal>

      <ConfirmModal open={discardConfirmOpen} setOpen={setDiscardConfirmOpen}
        title={"Discard staged changes?"}
        message={"This will remove the staged changes from the selected listings.\nThe live eBay listings are unaffected."}
        confirmButtonText={"Discard"}
        onConfirm={handleDiscard}
        isLoading={discardChanges.isLoading}
      />

      <ConfirmModal open={relistConfirmOpen} setOpen={setRelistConfirmOpen}
        title={"Send listings to be relisted?"}
        message={"Staged changes are applied first, then each listing is withdrawn from eBay and published again as a new listing.\nListings without staged changes are relisted as they are."}
        confirmButtonText={"Relist"}
        onConfirm={handleSendToRelist}
        isLoading={sendToRelist.isLoading}
      >
        <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={relistAppendSku}
            onChange={(e) => setRelistAppendSku(e.target.checked)}
          />
          <span>
            <span className="font-medium">Append SKU</span>
            <span className="block text-xs text-gray-500">
              Publish with a "-r1", "-r2", etc. suffix on the SKU to improve marketplace visibility. Tag numbers and scanning are unaffected.
            </span>
          </span>
        </label>
      </ConfirmModal>

      <BulkRelistResultModal
        open={relistResultOpen}
        setOpen={setRelistResultOpen}
        result={relistResult}
      />

      <form className="my-2 mb-4 space-y-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <VehiclePartSearchFilters
          key={vehicleFiltersKey}
          register={registerFilters}
          setValue={setFilterValue}
          getValues={getFilterValues}
          watch={watchFilters}
          categories={allCategories.data?.data ?? []}
          categoriesLoading={allCategories.isLoading}
          disabled={isSearchDisabled}
          isSearching={isSearching}
          setIsSearching={setIsSearching}
          isSmartMatch={isSmartMatch}
          setIsSmartMatch={setIsSmartMatch}
          vehicleDetailFilterEnabled={vehicleDetailFilterEnabled}
          setVehicleDetailFilterEnabled={setVehicleDetailFilterEnabled}
          onApplySmartSearch={setAdditionalBody}
        />

        <hr className="mt-1" />

        <TableFilterPanel
          activeFilterPills={activeFilterPills}
          disabled={isSearchDisabled}
          defaultFiltersStorageKey="wilma:tableFilters:ebay-optimisation"
          getDefaultFilters={getDefaultFilters}
          onApplyDefaultFilters={applyDefaultFilters}
        >
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <WarehouseLocationSearch
              showSearchOptions={true}
              register={registerFilters}
              setValue={setFilterValue}
              watch={watchFilters}
              isSearchDisabled={isSearchDisabled}
              warehouseLocations={warehouseLocations}
            />

            <DateRangeFilter
              dates={dates}
              onDatesChange={setDates}
              dateFieldType={dateFieldType}
              onDateFieldTypeChange={setDateFieldType}
              disabled={isSearchDisabled}
            />

            <TableDateSortControl
              sorting={sorting}
              onSortingChange={setSorting}
              disabled={isSearchDisabled}
            />

            {onToggleMissingField && (
              <div className="flex flex-col">
                <span className="text-sm font-medium text-black">Missing</span>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {MISSING_FIELD_OPTIONS.map((option) => {
                    const active = selectedMissingFields.includes(option.key);
                    return (
                      <button
                        key={option.key}
                        type="button"
                        disabled={isSearchDisabled}
                        onClick={() => onToggleMissingField(option.key)}
                        className={classNames(
                          "rounded-full border px-2.5 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
                          active
                            ? "border-primary-600 bg-primary-600 text-white"
                            : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </TableFilterPanel>

        <ButtonInput
          label="Clear"
          classes={classNames(isSearchDisabled ? "" : "!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-100")}
          onClick={handleClearFilters}
          disabled={isSearchDisabled}
        />
      </form>

      <PagedTable
        url="/part/listings"
        pageMode={PageMode.Listed}
        columns={cols}
        queryLabel="Search part details"
        showTotal={true}
        additionalBody={additionalBody}
        sorting={sorting}
        onSortingChange={setSorting}
        extraRow={(part: IPart) => {
          const stagedTitle = optimisationsByPartId.get(part.id)?.newTitle ?? undefined;
          const effectiveTitle = stagedTitle ?? part.offers?.[0]?.title ?? undefined;
          return (
            <div className="flex flex-col">
              <UnlistedEbayTitle
                part={part}
                title={effectiveTitle}
                onSaveTitle={(title) => handleStageTitle(part, title)}
              />
              <WilmaTitleRow
                part={part}
                currentTitle={effectiveTitle}
                onSaveTitle={(title) => handleStageTitle(part, title)}
                isBusy={stageChanges.isLoading}
              />
            </div>
          );
        }}
        extraRowPosition="top"
        expandedRow={expandedRow}
        actionButtons={[
          // The staging actions live behind one toggle; Discard and Send to Relist stay
          // outside the group so the two end-of-workflow actions are always one click away.
          {
            label: "Bulk Actions",
            onClick: () => { },
            groupedButtons: [
              { label: "Get Intelligence", onClick: (e: RowModel<IPart>) => handleBulkRunIntelligence(e), isLoading: intelligenceRunning, disabledOnNoRows: true },
              { label: "Stage Policies", onClick: (e: RowModel<IPart>) => handleOpenPolicyModal(e), isLoading: stageChanges.isLoading, disabledOnNoRows: true },
              { label: "Update Aspects", onClick: (e: RowModel<IPart>) => handleOpenAspectsModal(e), disabledOnNoRows: true },
              { label: "Reset Description", onClick: (e: RowModel<IPart>) => handleBulkResetDescription(e), isLoading: bulkUpdateProductDescription.isLoading, disabledOnNoRows: true },
              { label: "Stage Wilma Titles", onClick: (e: RowModel<IPart>) => handleBulkStageWilmaTitles(e), isLoading: stageChanges.isLoading, disabledOnNoRows: true },
              { label: "Stage Apply Image Rules", onClick: (e: RowModel<IPart>) => handleBulkStageFlag(e, "applyImageRules"), isLoading: stageChanges.isLoading, disabledOnNoRows: true },
              { label: "Stage Remove BG 1st Image", onClick: (e: RowModel<IPart>) => handleBulkStageFlag(e, "removeBackgroundFirstImage"), isLoading: stageChanges.isLoading, disabledOnNoRows: true },
              { label: "Stage Apply Defaults", onClick: (e: RowModel<IPart>) => handleBulkStageFlag(e, "applyCategoryDefaults"), isLoading: stageChanges.isLoading, disabledOnNoRows: true },
            ],
          },
          { label: "Discard Changes", onClick: (e: RowModel<IPart>) => handleOpenDiscardConfirm(e), isLoading: discardChanges.isLoading, disabledOnNoRows: true, classes: "!bg-red-600 hover:!bg-red-700" },
          { label: "Send to Relist", onClick: (e: RowModel<IPart>) => handleOpenRelistConfirm(e), isLoading: sendToRelist.isLoading, disabledOnNoRows: true, classes: "!bg-green-600 hover:!bg-green-700" },
        ]}
      />
    </>
  );
}
